import { Reference } from '@approvalmax/types';
import uniqBy from 'lodash/uniqBy';
import { domain } from 'modules/data';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';
import { useGetFields } from 'shared/data/webApp/v1';

import { normalizeValues } from './FilterReferenceList.helpers';
import { FilterReferenceListProps } from './FilterReferenceList.types';

export const useFilterReferenceList = (props: FilterReferenceListProps) => {
    const { columnKind, field, options: optionsStatic, onFilterChange, filter } = props;

    const isAsyncFilter = optionsStatic.length === 0 && columnKind !== 'customFields';

    const [query, setQuery] = useState('');
    const [referenceSearchValue, setReferenceSearchValue] = useState('');

    useDebounce(() => setQuery(referenceSearchValue), 500, [setQuery, referenceSearchValue]);

    const companyId = field ? field.companyId : '';

    const { optionsAsync, handleLoadOptionsAsync, isLoading } = useFilterAsyncField(
        isAsyncFilter,
        companyId,
        field,
        query
    );

    /**
     * This code is mostly needed when field operates asynchronously
     * n such cases, some options may not have been loaded in a first chunk, so selected values are not able to be displayed
     * (field can render only selected options from the list)
     *
     * Additionally, duplicates are not allowed as they would trigger warnings, so all IDs must be unique.
     */
    const options = useMemo<Reference[]>(() => {
        const optionsSelected = filter.values;
        const optionsResult = optionsAsync || optionsStatic || [];

        const optionsIds = new Set(optionsResult.map((option) => option.id));

        return optionsSelected.reduce((total, option) => {
            if (!optionsIds.has(option.id)) {
                total.push(option);
                optionsIds.add(option.id);
            }

            return total;
        }, optionsResult);
    }, [filter.values, optionsAsync, optionsStatic]);

    const value = useMemo(() => filter.values.map((filterValue: Reference) => filterValue.id), [filter.values]);

    const handleChange = useCallback(
        (selectedIds: string[]) => {
            const values = options.filter((option) => selectedIds.includes(option.id));

            onFilterChange({ ...filter, values });
        },
        [filter, onFilterChange, options]
    );

    const handleOpen = useCallback(
        async (open: boolean) => {
            if (open && isAsyncFilter && !optionsAsync) {
                await handleLoadOptionsAsync();
            }
        },
        [isAsyncFilter, optionsAsync, handleLoadOptionsAsync]
    );

    return {
        value,
        isLoading,
        handleOpen,
        handleChange,
        handleInputChange: setReferenceSearchValue,
        options,
    };
};

export const useFilterAsyncField = (enabled: boolean, companyId: string, field?: domain.Field, query?: string) => {
    const [optionsAsync, setOptionsAsync] = useState<Reference[] | null>(null);

    const { data, isFetching, isSuccess, refetch } = useGetFields(
        {
            query: {
                companyId,
                withValues: true,
                fieldsId: [field ? field.id : ''],
                startFrom: 0,
                rowNum: 116,
                allFields: true,
                /*
                    TODO: find a better solution
                    NetSuite requires an integrationCode to return a list of customers
                    "NetSuite-invoice" is hardcoded for now
                */
                integrationCode:
                    field?.systemPurpose === domain.FieldSystemPurpose.NetSuiteCustomer
                        ? domain.IntegrationCode.NetSuiteInvoice
                        : null,
                query,
            },
        },
        {
            enabled: enabled && !!query,
        }
    );

    const handleLoadOptionsAsync = useCallback(async () => {
        switch (field?.systemPurpose) {
            case domain.FieldSystemPurpose.AirwallexBatchPaymentStatus:
            case domain.FieldSystemPurpose.AirwallexBeneficiary:
            case domain.FieldSystemPurpose.AirwallexCurrency:
            case domain.FieldSystemPurpose.AirwallexFeeAmount:
            case domain.FieldSystemPurpose.AirwallexPaymentFee:
            case domain.FieldSystemPurpose.AirwallexPaymentItemStatus:
            case domain.FieldSystemPurpose.AirwallexPaymentMethod:
            case domain.FieldSystemPurpose.AirwallexPaymentPurpose:
            case domain.FieldSystemPurpose.AirwallexSourceAmount:
            case domain.FieldSystemPurpose.AmaxPayBankAccount:
            case domain.FieldSystemPurpose.DearAccount:
            case domain.FieldSystemPurpose.DearBrand:
            case domain.FieldSystemPurpose.DearLocation:
            case domain.FieldSystemPurpose.DearProduct:
            case domain.FieldSystemPurpose.DearProductCategory:
            case domain.FieldSystemPurpose.DearProductFamily:
            case domain.FieldSystemPurpose.DearSupplier:
            case domain.FieldSystemPurpose.NetSuiteAccount:
            case domain.FieldSystemPurpose.NetSuiteClass:
            case domain.FieldSystemPurpose.NetSuiteCurrency:
            case domain.FieldSystemPurpose.NetSuiteCustom:
            case domain.FieldSystemPurpose.NetSuiteCustomer:
            case domain.FieldSystemPurpose.NetSuiteDepartment:
            case domain.FieldSystemPurpose.NetSuiteEmployee:
            case domain.FieldSystemPurpose.NetSuiteExpenseCategory:
            case domain.FieldSystemPurpose.NetSuiteItem:
            case domain.FieldSystemPurpose.NetSuiteLocation:
            case domain.FieldSystemPurpose.NetSuitePayee:
            case domain.FieldSystemPurpose.NetSuiteSubsidiary:
            case domain.FieldSystemPurpose.NetSuiteTaxCode:
            case domain.FieldSystemPurpose.NetSuiteVendor:
            case domain.FieldSystemPurpose.QBooksAccount:
            case domain.FieldSystemPurpose.QBooksClass:
            case domain.FieldSystemPurpose.QBooksCurrency:
            case domain.FieldSystemPurpose.QBooksCustomer:
            case domain.FieldSystemPurpose.QBooksDepartment:
            case domain.FieldSystemPurpose.QBooksDocumentStatus:
            case domain.FieldSystemPurpose.QBooksEmployee:
            case domain.FieldSystemPurpose.QBooksInvoiceCustomer:
            case domain.FieldSystemPurpose.QBooksPayeeCustomer:
            case domain.FieldSystemPurpose.QBooksPayeeEmployee:
            case domain.FieldSystemPurpose.QBooksPayeeVendor:
            case domain.FieldSystemPurpose.QBooksPaymentAccount:
            case domain.FieldSystemPurpose.QBooksPaymentMethod:
            case domain.FieldSystemPurpose.QBooksPaymentType:
            case domain.FieldSystemPurpose.QBooksPoItem:
            case domain.FieldSystemPurpose.QBooksSalesItem:
            case domain.FieldSystemPurpose.QBooksTax:
            case domain.FieldSystemPurpose.QBooksVendor:
            case domain.FieldSystemPurpose.XeroAccount:
            case domain.FieldSystemPurpose.XeroBankAccount:
            case domain.FieldSystemPurpose.XeroBranding:
            case domain.FieldSystemPurpose.XeroContactDefaultCurrency:
            case domain.FieldSystemPurpose.XeroContactPurchaseAccount:
            case domain.FieldSystemPurpose.XeroContactPurchaseTax:
            case domain.FieldSystemPurpose.XeroContactPurchaseTrackingCategories:
            case domain.FieldSystemPurpose.XeroContactSalesAccount:
            case domain.FieldSystemPurpose.XeroContactSalesTax:
            case domain.FieldSystemPurpose.XeroContactSalesTrackingCategories:
            case domain.FieldSystemPurpose.XeroCurrency:
            case domain.FieldSystemPurpose.XeroCustomer:
            case domain.FieldSystemPurpose.XeroItem:
            case domain.FieldSystemPurpose.XeroLineAmountType:
            case domain.FieldSystemPurpose.XeroSupplier:
            case domain.FieldSystemPurpose.XeroTax:
            case domain.FieldSystemPurpose.XeroTracking:
                await refetch();
                break;

            case domain.FieldSystemPurpose.General:
            case domain.FieldSystemPurpose.Requester:
            default:
                break;
        }
    }, [field?.systemPurpose, refetch]);

    useEffect(() => {
        if (field && data && isSuccess) {
            const fieldData = data.fields.find((dataField) => dataField.fieldId === field.id);

            if (fieldData && fieldData.exactValues) {
                setOptionsAsync((valuesPrev) => {
                    const valuesNext = normalizeValues(fieldData.exactValues, fieldData.systemPurpose).map<Reference>(
                        (exactValue) => ({
                            id: exactValue.fieldValueId,
                            text: exactValue.value,
                        })
                    );

                    const values = [...(valuesPrev || []), ...valuesNext];

                    return uniqBy(values, (value) => value.id);
                });
            }
        }
    }, [data, field, isSuccess]);

    return {
        isLoading: isFetching,
        optionsAsync,
        handleLoadOptionsAsync,
    };
};
